import * as H from 'history';
import { Button } from '@modernhealth/vital-components';
import React from 'react';
import { HpiEligibilityFlow } from 'src/shared/models/shared';
import { FlowLayout } from '../components/FlowLayout';
import { ActionGenerator, ActionType } from './ActionsGenerator';
import { FooterGenerator, FooterType } from './FooterGenerator';
import { IllustrationGenerator, IllustrationType } from './IllustrationGenerator';
import { getSchema, InsurancePageName } from '../helpers/insuranceHelpers';
import { useTranslation } from 'react-i18next';
import { useHpiEligibilityFlow } from '../hooks/useHpiEligibilityFlow';
import { useSelector } from 'react-redux';
import { getMyTherapists } from 'src/modules/providers/selectors/providersSelectors';
import { useHpiEligibilityFlowAnalytics } from '../analytics';
import { CoachNode } from 'src/generated/webapp_gql';
import { Property } from 'csstype';

type ActionScheme = {
    title: string;
    onClick: ActionType;
    disabled?: boolean;
};

export type FlowLayoutSchema = {
    illustrationType: IllustrationType;
    titleAlignment?: Property.TextAlign;
    heading: string;
    actionSchema: ActionScheme[];
    footer?: FooterType;
};

interface InsuranceControllerProxyProps {
    page: InsurancePageName;
    location: H.Location<{
        session?: string | undefined;
        routedFromProfilePage?: boolean | undefined;
        hpiEligibilityFlow?: HpiEligibilityFlow | undefined;
    }>;
}

const InsuranceControllerProxy: React.FC<InsuranceControllerProxyProps> = ({
    page,
    location,
    children,
}) => {
    const myTherapists: CoachNode[] = useSelector(getMyTherapists);
    const { t } = useTranslation();

    const session = location?.state?.session;
    const routedFromProfilePage = location?.state?.routedFromProfilePage ?? false;
    const hpiEligibilityFlow = useHpiEligibilityFlow();

    const schema = getSchema(page, hpiEligibilityFlow, routedFromProfilePage, t, session);
    const { trackClick } = useHpiEligibilityFlowAnalytics(page, hpiEligibilityFlow);

    const illustration = <IllustrationGenerator illustrationType={schema.illustrationType} />;
    const titleAlignment = !!schema.titleAlignment ? schema.titleAlignment : undefined;
    const heading = schema.heading;
    const actions: Action[] = schema.actionSchema.map(actionScheme => {
        return {
            ...actionScheme,
            onClick: ActionGenerator({
                page,
                actionTitle: actionScheme.title,
                actionType: actionScheme.onClick,
                location,
                myTherapists,
                trackClick,
            }),
        };
    });
    const footer = !!schema.footer ? <FooterGenerator footerType={schema.footer} /> : undefined;

    return (
        <InsuranceController
            illustration={illustration}
            titleAlignment={titleAlignment}
            heading={heading}
            actions={actions}
            footer={footer}
        >
            {children}
        </InsuranceController>
    );
};

type Action = {
    title: string;
    onClick?: VoidFunction;
    disabled?: boolean;
};

interface InsuranceControllerProps {
    illustration: React.ReactNode;
    heading: string;
    titleAlignment?: Property.TextAlign;
    actions: Action[];
    footer?: React.ReactNode;
}

const InsuranceController: React.FC<InsuranceControllerProps> = ({
    illustration,
    heading,
    titleAlignment,
    actions,
    children,
    footer,
}) => {
    const actionComponents = actions.map((action, idx) => {
        let variant,
            style,
            fluid = false;
        if (actions.length > 2 && idx === actions.length - 1) {
            variant = 'flat';
            style = undefined;
            fluid = true;
        } else if (actions.length > 1 && idx === 0) {
            variant = 'secondary';
            style = { flex: 1 };
        } else {
            variant = 'primary';
            style = { flex: 1 };
        }

        return (
            <Button
                key={idx}
                variant={variant}
                size="large"
                onClick={action.onClick}
                {...(fluid && { fluid })}
                {...(style && { style })}
                {...(action.disabled && { disabled: action.disabled })}
            >
                {action.title}
            </Button>
        );
    });

    return (
        <FlowLayout
            illustration={illustration}
            heading={heading}
            headingAlign={titleAlignment || 'center'}
            actions={actionComponents}
            footer={footer}
        >
            {children}
        </FlowLayout>
    );
};

export default InsuranceControllerProxy;
